export type FeedbackDeliveryStatus = 'sent' | 'not-configured' | 'failed' | 'unavailable';

export interface FeedbackAuthorityResponse {
  ok: boolean;
  status: number;
  body: {
    email?: FeedbackDeliveryStatus;
    error?: string;
    statusRevision?: number;
  };
}

export const FEEDBACK_FUNCTION_TIMEOUT_MS = 12_000;

export async function withFeedbackDeadline<T>(
  operation: () => Promise<T>,
  timeoutMs = FEEDBACK_FUNCTION_TIMEOUT_MS,
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => reject(new Error('Feedback request timed out.')), timeoutMs);
  });
  const request = Promise.resolve().then(operation);
  try {
    return await Promise.race([request, timeout]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

export async function fetchFeedbackFunctionWithTimeout(
  fetchImpl: typeof fetch,
  input: RequestInfo | URL,
  init: RequestInit,
  timeoutMs = FEEDBACK_FUNCTION_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort();
      reject(new Error('Feedback request timed out.'));
    }, timeoutMs);
  });
  const request = Promise.resolve().then(() => fetchImpl(input, {
    ...init,
    signal: controller.signal,
  }));
  try {
    return await Promise.race([request, timeout]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

export async function requestFeedbackAuthority(input: {
  fetchImpl: typeof fetch;
  getHeaders: () => Promise<HeadersInit>;
  body: Record<string, unknown>;
  timeoutMs?: number;
}): Promise<FeedbackAuthorityResponse> {
  const timeoutMs = input.timeoutMs ?? FEEDBACK_FUNCTION_TIMEOUT_MS;
  const deadlineAt = Date.now() + timeoutMs;
  const remaining = () => {
    const value = deadlineAt - Date.now();
    if (value <= 0) throw new Error('Feedback request timed out.');
    return value;
  };
  const headers = await withFeedbackDeadline(input.getHeaders, remaining());
  const response = await fetchFeedbackFunctionWithTimeout(
    input.fetchImpl,
    '/.netlify/functions/feedbackNotify',
    {
      method: 'POST',
      headers,
      body: JSON.stringify(input.body),
    },
    remaining(),
  );
  let body: FeedbackAuthorityResponse['body'] = {};
  try {
    body = await withFeedbackDeadline(
      () => response.json() as Promise<FeedbackAuthorityResponse['body']>,
      remaining(),
    );
  } catch (error) {
    if (error instanceof Error && /timed out/i.test(error.message)) throw error;
    // The HTTP status still supplies a safe failure path if a proxy replaces
    // the function's JSON response.
  }
  return { ok: response.ok, status: response.status, body };
}

export type FeedbackSubmissionAuthorityOutcome =
  | { ok: true; email: FeedbackDeliveryStatus; recovered: boolean }
  | { ok: false; error?: string; status: number };

export async function submitFeedbackWithRecovery(
  reportId: string,
  deps: {
    submit: (reportId: string) => Promise<FeedbackAuthorityResponse>;
    reportExists: (reportId: string) => Promise<boolean>;
    notify: (reportId: string) => Promise<FeedbackDeliveryStatus>;
  },
  stageTimeoutMs = FEEDBACK_FUNCTION_TIMEOUT_MS,
): Promise<FeedbackSubmissionAuthorityOutcome> {
  let lastFailure: { error?: string; status: number } = { status: 503 };
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const result = await deps.submit(reportId);
      if (result.ok) {
        return { ok: true, email: result.body.email ?? 'failed', recovered: false };
      }
      lastFailure = { error: result.body.error, status: result.status };
      if (result.status < 500 && result.status !== 429) {
        return { ok: false, ...lastFailure };
      }
    } catch {
      // A timeout or lost connection is ambiguous. Retry the same report id,
      // then prove whether the server transaction committed before failing.
    }
  }

  try {
    if (await withFeedbackDeadline(() => deps.reportExists(reportId), stageTimeoutMs)) {
      try {
        const email = await withFeedbackDeadline(() => deps.notify(reportId), stageTimeoutMs);
        return { ok: true, email, recovered: true };
      } catch {
        // The report is durable. Return success and let the outbox retry the
        // notification instead of leaving the form stuck or inviting a
        // duplicate report.
        return { ok: true, email: 'unavailable', recovered: true };
      }
    }
  } catch {
    // Preserve the last actionable authority failure below.
  }
  return { ok: false, ...lastFailure };
}
